import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { setSessionWithExpiry, getValidSession } from '../helpers/sessionUtils';
import useWebOTP from '../../hooks/useWebOTP';
import OtpInputBox from '../../components/common/OtpInputBox';

import { COUNTRIES } from '../../utils/countryData';

const OTP_VALID_SECONDS = 5 * 60;

// Simple message display
const FormMessageDisplay = ({ message, type }) => {
  if (!message) return null;
  const messageClass =
    type === 'error' ? 'form-message-error' : type === 'success' ? 'form-message-success' : 'form-message-info';
  return (
    <div 
      className={`form-message-display ${messageClass} mb-3`} 
      role="alert"
      dangerouslySetInnerHTML={{ __html: message }}
    />
  );
};

/**
 * LoginPopup
 * - Accepts optional props:
 *    visible: boolean          -> if provided, controls visibility from parent
 *    onClose: function         -> called when popup is closed (so parent can update its state)
 *    onLoginSuccess: function  -> called with user data after successful login (skips page reload)
 */
function LoginPopup({ visible, isOpen, onClose, onLoginSuccess, autoShow = false }) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

  const [selectedCountry, setSelectedCountry] = useState(COUNTRIES[0]);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const countryDropdownRef = useRef(null);

  const [phone, setPhone] = useState('');
  const location = useLocation();
  const [showPopup, setShowPopup] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [serviceType, setServiceType] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [remainingTime, setRemainingTime] = useState(OTP_VALID_SECONDS);
  const timerRef = useRef(null);
  const verifyInFlightRef = useRef(false);
  const [isOtpFormValid, setIsOtpFormValid] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [userIdToVerify, setUserIdToVerify] = useState('');
  const [userTypeToVerify, setUserTypeToVerify] = useState('visitor');
  const [formMessage] = useState({ text: '', type: '' });

  // Determine whether popup should be visible.
  // We check both 'visible' and 'isOpen' props for compatibility with various pages.
  const isVisible = visible === true || isOpen === true || showPopup === true;

  // service type and inject stylesheet (same as before)
  useEffect(() => {
    if (
      location.pathname === '/holidays' ||
      location.pathname === '/holidays-list' ||
      location.pathname === '/holiday-details'
    ) {
      setServiceType('holidays');
    } else if (
      location.pathname === '/hotels' ||
      location.pathname === '/hotel-list' ||
      location.pathname === '/hotel-details'
    ) {
      setServiceType('hotels');
    } else if (location.pathname === '/cabs') {
      setServiceType('cabs');
    } else if (location.pathname === '/tirthyatra' || location.pathname === '/yatra-list' || location.pathname.startsWith('/yatra-details')) {
      setServiceType('tirth-yatra');
    } else if (location.pathname === '/medicaltourism') {
      setServiceType('medical-tourism');
    }

    const themeStylesheet = document.getElementById('loginpopup-css');
    if (!themeStylesheet) {
      const link = document.createElement('link');
      link.id = 'loginpopup-css';
      link.rel = 'stylesheet';
      link.href = '/assets/css/LoginPopup.css';
      document.head.appendChild(link);
    }
  }, [location]);

  // Global auto-show logic with persistent timer
  useEffect(() => {
    //console.log("LoginPopup mounted/updated. autoShow:", autoShow, "Path:", location.pathname);

    // Only run auto-show logic if enabled via prop
    if (!autoShow) return;

    let timeoutId;
    const validUserSession = getValidSession();
    //console.log("Valid session found?", !!validUserSession);

    // Check for explicit public paths and detail patterns
    const isPublicPath = [
      '/', '/holidays', '/hotels', '/cabs', '/tirthyatra', '/medicaltourism',
      '/holidays-list', '/hotel-list', '/cabs/search', '/search', '/yatra-list'
    ].includes(location.pathname) ||
      location.pathname.startsWith('/holiday-details/') ||
      location.pathname.startsWith('/hotel-details/') ||
      location.pathname.startsWith('/medical-details/') ||
      location.pathname.startsWith('/yatra-details/');

    const shouldShowOnPath = isPublicPath;
    //console.log("Should show on this path?", shouldShowOnPath);

    // We only want to auto-show if not logged in and on a public path
    if (shouldShowOnPath && !validUserSession) {
      // Use sessionStorage to track the 5-second mark across page navigations
      let sessionStart = sessionStorage.getItem('loginPopupFirstVisit');
      if (!sessionStart) {
        sessionStart = Date.now().toString();
        sessionStorage.setItem('loginPopupFirstVisit', sessionStart);
        //console.log("Started new visit timer at:", sessionStart);
      }

      const elapsed = Date.now() - parseInt(sessionStart);
      const remaining = Math.max(0, 5000 - elapsed);
      //console.log(`Elapsed: ${elapsed}ms, Remaining: ${remaining}ms`);

      timeoutId = setTimeout(() => {
        //console.log("Timer finished. Re-checking session...");
        // Re-check session right before showing
        if (!getValidSession()) {
          //console.log("No session found. SHOWING POPUP.");
          setShowPopup(true);
        } else {
          //console.log("Session found at last second. Aborting.");
        }
      }, remaining);
    } else if (!shouldShowOnPath) {
      // Hide on non-public paths (auth, admin)
      setShowPopup(false);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [location.pathname, autoShow]);

  // Listen for global trigger events
  useEffect(() => {
    const handleTrigger = (event) => {
      if (event.detail && event.detail.show !== undefined) {
        setShowPopup(event.detail.show);
      } else {
        setShowPopup(true);
      }
    };

    window.addEventListener('toggleLoginPopup', handleTrigger);
    return () => window.removeEventListener('toggleLoginPopup', handleTrigger);
  }, []);

  // Listen for click outside country dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setIsCountryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // form validation effects
  useEffect(() => {
    const isPhoneValid = selectedCountry.code === '+91'
      ? phone.trim().length === 10
      : phone.trim().length >= 7 && phone.trim().length <= 15;
    setIsFormValid(name.trim() !== '' && isPhoneValid);
  }, [name, phone, selectedCountry]);

  useEffect(() => {
    if (otpSent && remainingTime > 0) {
      timerRef.current = setInterval(() => {
        setRemainingTime((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(timerRef.current);
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    } else if (remainingTime === 0) {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [otpSent, remainingTime]);

  useEffect(() => {
    setIsOtpFormValid(otpDigits.every((digit) => digit !== ''));
  }, [otpDigits]);

  // helper to close popup and also notify parent if provided
  const closePopup = () => {
    setShowPopup(false);
    setOtpSent(false);
    setOtpDigits(['', '', '', '', '', '']);
    setRemainingTime(OTP_VALID_SECONDS);
    setIsVerifyingOtp(false);
    verifyInFlightRef.current = false;
    if (typeof onClose === 'function') onClose();
  };

  const handleMobileSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) {
      alert('Please fill in your name and a valid mobile number.');
      return;
    }

    const mobileNumber = selectedCountry.code === '+91'
      ? phone
      : `${selectedCountry.code}${phone.replace(/\D/g, '')}`;
    const userDataPayload = {
      name: name,
      phone: mobileNumber,
      email: email || null,
      role_id: 3,
    };

    try {
      const registerAndSendOtpResponse = await fetch(`${API_BASE_URL}/users/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userDataPayload),
      });

      const responseData = await registerAndSendOtpResponse.json();

      if (registerAndSendOtpResponse.ok && responseData.status === 'otp_sent') {
        setOtpSent(true);
        setUserIdToVerify(responseData.user.id);
        setUserTypeToVerify(responseData.userType || 'visitor');
        setRemainingTime(60);
        console.log('Registration/Login initiated, OTP Sent. UserID:', responseData.user.id, 'UserType:', responseData.userType);
      } else {
        alert(`Failed: ${responseData.message || 'An error occurred. Please try again.'}`);
      }
    } catch (error) {
      console.error('Error during registration/OTP sending request:', error);
      alert('Failed to process request. Please check your network and try again.');
    }
  };

  const handleOtpSubmit = async (e, otpOverride = null) => {
    if (e) e.preventDefault();
    const enteredOtp = otpOverride || otpDigits.join('');

    if (verifyInFlightRef.current) return;

    if (remainingTime <= 0) {
      alert('OTP has expired. Please request a new OTP.');
      return;
    }

    if (!userIdToVerify) {
      alert('User session error. Please try sending OTP again by re-entering your mobile number.');
      setOtpSent(false);
      return;
    }

    verifyInFlightRef.current = true;
    setIsVerifyingOtp(true);

    try {
      const loginVerifyResponse = await fetch(`${API_BASE_URL}/users/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: userIdToVerify, otp: enteredOtp, userType: userTypeToVerify, phone }),
      });

      const loginData = await loginVerifyResponse.json();

      if (loginVerifyResponse.ok && loginData.status === 'otp_verified_login_successful') {
        const userDataToStore = {
          ...(loginData.user || {}),
          isVendor: !!loginData.isVendor,
          isVisitor: !!loginData.isVisitor,
        };
        const token = loginData.token;

        if (userDataToStore && userDataToStore.id && token) {
          // Store session appropriately for vendor or visitor
          if (loginData.isVendor) {
            localStorage.setItem('userInfo', JSON.stringify(userDataToStore));
            localStorage.removeItem('visitorUser');
          } else {
            localStorage.setItem('visitorUser', JSON.stringify(userDataToStore));
            localStorage.removeItem('userInfo');
          }

          localStorage.setItem('authToken', token);
          localStorage.setItem('token', token);

          setSessionWithExpiry({ user: userDataToStore, token: token });
          closePopup();
          if (typeof onLoginSuccess === 'function') {
            onLoginSuccess(userDataToStore);
          } else {
            window.location.reload();
          }
        } else {
          console.error('Login data incomplete:', loginData);
          alert('Login succeeded but user data or token seems incomplete.');
        }
      } else {
        alert(`OTP Verification Failed: ${loginData.message || 'Incorrect OTP or error.'}`);
      }
    } catch (error) {
      console.error('Error during OTP verification with backend:', error);
      alert('Failed to verify OTP. Please check your network and try again.');
    } finally {
      verifyInFlightRef.current = false;
      setIsVerifyingOtp(false);
    }
  };

  const handleMaybeLater = () => {
    closePopup();
  };

  // WebOTP API SMS Auto-Detection for mobile browsers
  useWebOTP({
    enabled: isVisible && otpSent && remainingTime > 0,
    onOtpReceived: (code) => {
      if (code && code.length === 6) {
        const digits = code.split('');
        setOtpDigits(digits);
        const inputs = document.querySelectorAll('.otp-digit-input');
        if (inputs[5]) inputs[5].focus();
        handleOtpSubmit(null, code);
      }
    },
  });

  const handleDigitChange = (index, value) => {
    const rawStr = String(value || '');
    const match = rawStr.match(/\b\d{6}\b/);
    const cleaned = match ? match[0] : rawStr.replace(/\D/g, '');
    if (cleaned.length > 1) {
      let newOtpDigits;
      if (cleaned.length >= 6) {
        newOtpDigits = cleaned.slice(0, 6).split('');
      } else {
        newOtpDigits = [...otpDigits];
        cleaned.slice(0, 6).split('').forEach((d, i) => {
          if (index + i < 6) {
            newOtpDigits[index + i] = d;
          }
        });
      }
      setOtpDigits(newOtpDigits);
      const lastIndex = Math.min(5, newOtpDigits.length - 1);
      const inputs = document.querySelectorAll('.otp-digit-input');
      if (inputs[lastIndex]) inputs[lastIndex].focus();
      if (newOtpDigits.every((digit) => digit !== '')) {
        handleOtpSubmit(null, newOtpDigits.join(''));
      }
      return;
    }

    const singleDigit = cleaned.slice(-1);
    const newOtpDigits = [...otpDigits];
    newOtpDigits[index] = singleDigit;
    setOtpDigits(newOtpDigits);

    if (singleDigit && index < 5) {
      const nextInput = document.querySelectorAll(`.otp-digit-input`)[index + 1];
      if (nextInput) nextInput.focus();
    }
    if (newOtpDigits.every((digit) => digit !== '')) {
      handleOtpSubmit(null, newOtpDigits.join(''));
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData ? e.clipboardData.getData('text') : '';
    const cleaned = pastedData.replace(/\D/g, '').slice(0, 6);
    if (cleaned.length > 0) {
      const digits = cleaned.split('');
      const newOtpDigits = Array(6).fill('');
      digits.forEach((d, i) => { newOtpDigits[i] = d; });
      setOtpDigits(newOtpDigits);
      const inputs = document.querySelectorAll('.otp-digit-input');
      if (inputs[digits.length - 1]) inputs[digits.length - 1].focus();
      if (cleaned.length === 6) {
        handleOtpSubmit(null, cleaned);
      }
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      const inputs = document.querySelectorAll('.otp-digit-input');
      if (inputs[index - 1]) inputs[index - 1].focus();
    }
  };

  const handleResendOtp = () => {
    if (phone && name) {
      handleMobileSubmit({ preventDefault: () => { } });
      setOtpDigits(['', '', '', '', '', '']);
      setIsVerifyingOtp(false);
      verifyInFlightRef.current = false;
    } else {
      alert('Name and Mobile number are required to resend OTP. Please fill the form again.');
      setOtpSent(false);
      setUserIdToVerify('');
    }
  };

  // If neither internal show nor parent visible, do not render
  if (!isVisible) return null;

  // Styles (you can keep CSS file; inline styles here are fallback)
  const buttonStyle = {
    cursor: isFormValid ? 'pointer' : 'not-allowed',
    backgroundColor: isFormValid ? 'rgb(9, 156, 241)' : '#717171',
    color: 'white',
    padding: '10px 40px',
    borderRadius: '15px',
    border: isFormValid ? '1px solid rgb(9, 156, 241)' : '1px solid #717171',
    fontSize: '16px',
  };

  const otpButtonStyle = {
    ...buttonStyle,
    cursor: isOtpFormValid && !isVerifyingOtp && remainingTime > 0 ? 'pointer' : 'not-allowed',
    backgroundColor: isOtpFormValid && !isVerifyingOtp && remainingTime > 0 ? 'rgb(9, 156, 241)' : '#d3d3d3',
    border: isOtpFormValid && !isVerifyingOtp && remainingTime > 0 ? '1px solid rgb(9, 156, 241)' : '1px solid #d3d3d3',
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="popup-overlay">
      <div className="popup-content">
        <FormMessageDisplay message={formMessage.text} type={formMessage.type} />
        {!otpSent && (
          <div className="row">
            <p className="popup-subtitle">
              Get the list of best <span className="service-type">{serviceType}</span> service provider
            </p>
          </div>
        )}

        {!otpSent ? (
          <form onSubmit={handleMobileSubmit} className="horizontal-form">
            <div className="form-row">
              <div className="floating-label">
                <input type="text" id="name" value={name} onChange={(e) => setName(e.target.value)} required />
                <label htmlFor="name" className={name ? 'filled' : ''}>
                  Enter Your Name
                </label>
              </div>
              <div className="floating-label">
                <input type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                <label htmlFor="email" className={email ? 'filled' : ''}>
                  Enter Your Email (Optional)
                </label>
              </div>
            </div>

            <div className="mobile-number-container" ref={countryDropdownRef}>
              <label className="mobile-number-label">MOBILE NUMBER</label>
              <div className="mobile-input-wrapper">
                <button
                  type="button"
                  className="country-code-btn"
                  onClick={() => setIsCountryDropdownOpen(!isCountryDropdownOpen)}
                >
                  <img
                    src={`/admin-assets/images/flags/${selectedCountry.iso.toLowerCase()}.svg`}
                    alt={selectedCountry.name}
                    className="country-flag-img"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `https://flagcdn.com/w40/${selectedCountry.iso.toLowerCase()}.png`;
                    }}
                  />
                  <span className="country-dial-code">{selectedCountry.code}</span>
                  <span className="country-dropdown-arrow">▾</span>
                </button>
                <input
                  type="tel"
                  id="mobile"
                  name="mobile"
                  placeholder={selectedCountry.code === '+91' ? 'Enter 10-digit mobile number' : 'Enter mobile number'}
                  value={phone}
                  onChange={(e) => {
                    let val = e.target.value.replace(/\D/g, '');
                    if (selectedCountry.code === '+91') {
                      if (val.length > 10 && val.startsWith('91')) {
                        val = val.slice(val.length - 10);
                      } else {
                        val = val.slice(0, 10);
                      }
                    } else {
                      val = val.slice(0, 15);
                    }
                    setPhone(val);
                  }}
                  maxLength={selectedCountry.code === '+91' ? 10 : 15}
                  required
                  autoFocus
                  className="mobile-number-input"
                />
              </div>

              {isCountryDropdownOpen && (
                <div className="country-dropdown-menu">
                  <div className="country-search-box">
                    <input
                      type="text"
                      className="country-search-input"
                      placeholder="Search country or code..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="country-list-scroll">
                    {COUNTRIES.filter(c =>
                      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      c.code.includes(searchQuery) ||
                      c.iso.toLowerCase().includes(searchQuery.toLowerCase())
                    ).map((c) => (
                      <div
                        key={`${c.iso}-${c.code}`}
                        className={`country-option-item ${selectedCountry.iso === c.iso && selectedCountry.code === c.code ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedCountry(c);
                          setSearchQuery('');
                          setIsCountryDropdownOpen(false);
                        }}
                      >
                        <img
                          src={`/admin-assets/images/flags/${c.iso.toLowerCase()}.svg`}
                          alt={c.name}
                          className="country-flag-img"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://flagcdn.com/w40/${c.iso.toLowerCase()}.png`;
                          }}
                        />
                        <span className="country-option-name">{c.name}</span>
                        <span className="country-option-code">{c.code}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="button-container">
              <button type="submit" className="otp-button" disabled={!isFormValid} style={buttonStyle}>
                Login with OTP
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleOtpSubmit} className="otp-form">
            <h2 className="otp-verification">OTP Verification</h2>
            <p className="otp-paragraph">
              Please enter the OTP (One-Time Password) sent to your registered mobile number{' '}
              <span className="service-type">({selectedCountry.code} {phone})</span> to complete your verification.
            </p>

            <OtpInputBox
              otpDigits={otpDigits}
              onChange={(newDigits) => {
                setOtpDigits(newDigits);
              }}
              onComplete={(code) => {
                handleOtpSubmit(null, code);
              }}
              disabled={isVerifyingOtp}
              boxClassName="otp-digit-input"
              containerClassName="otp-input-container"
            />

            <div className="otp-timer">Remaining time: {formatTime(remainingTime)}</div>
            <div className="otp-resend">
              Didn't get the code?{' '}
              {remainingTime > 0 ? (
                <span style={{ color: '#aaa', cursor: 'not-allowed' }}>Resend</span>
              ) : (
                <a
                  href="#!"
                  onClick={(e) => {
                    e.preventDefault();
                    handleResendOtp();
                  }}
                  style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
                >
                  Resend
                </a>
              )}
            </div>

            <div className="button-container otp-button-group">
              <button
                type="submit"
                className="otp-button verify-button"
                disabled={!isOtpFormValid || isVerifyingOtp || remainingTime <= 0}
                style={otpButtonStyle}
              >
                {isVerifyingOtp ? 'Verifying...' : 'Verify'}
              </button>
              <button type="button" className="otp-button cancel-button" onClick={handleMaybeLater}>
                Cancel
              </button>
            </div>
          </form>
        )}

        <div className="maybe-later-link">
          <a
            href="#!"
            onClick={(e) => {
              e.preventDefault();
              handleMaybeLater();
            }}
          >
            Maybe Later
          </a>
        </div>
      </div>
    </div>
  );
}

export default LoginPopup;
